import { MantineProvider, Tabs, createStyles } from "@mantine/core";
import { Button, tokens } from "@blurb/codex-react";
import { OpenInNewIcon } from "@blurb/codex-react/icons";
import React from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import { Heading } from "../../components/Typography";
import { usePageShellStyles } from "../../components/pageShellStyles";
import { UseAuthUserResult, useAuthUser } from "../../hooks/useAuthUser";
import { getRoutePath } from "../../hooks/useNavigateRoute";
import { PublicUrl } from "../../utils/link.util";
import { RpiCard } from "../../rpi-core/card/RpiCard";
import { rpaTheme } from "../../theme/rpaTheme";
import { ApiSetupInstructionsBanner } from "./ApiSetupInstructionsBanner";
import { DEVELOPER_TOOLS_TABS } from "./developerToolsTabs";

const useStyles = createStyles(() => ({
  card: {
    display: "flex",
    flexDirection: "column",
    gap: tokens.spacing[6],
    padding: tokens.spacing[6],
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    gap: tokens.spacing[4],
  },

  // Figma "Tab Line H": 16px labels sitting 24px apart on a subtle rule, the active one bold above a
  // 3px bar. Mantine's defaults are a 14px label with its own horizontal padding and a 2px bar.
  tabsList: {
    gap: tokens.spacing[6],
    borderBottomWidth: 1,
    borderBottomColor: tokens.color.semantic.border.subtle,
  },

  tab: {
    padding: `${tokens.spacing[3]} 0`,
    fontSize: 16,
    color: tokens.color.semantic.text.bold,
    borderBottomWidth: 3,
    marginBottom: -1,
    // The bar is the design system's brand border, not Mantine's filled-primary shade (#5a349e).
    "&[data-active]": {
      fontWeight: 700,
      color: tokens.color.semantic.text.bold,
      borderBottomColor: tokens.color.semantic.border.primary,
    },
  },
}));

const DEFAULT_TAB = DEVELOPER_TOOLS_TABS[0].path;

function findActiveTab(pathname: string, basePath: string): string {
  return DEVELOPER_TOOLS_TABS.find((tab) => isTabActive(pathname, `${basePath}/${tab.path}`))?.path ?? DEFAULT_TAB;
}

// Match the tab's own path or a route nested under it (e.g. the Generate Key drawer), but not a
// sibling tab whose path merely shares this prefix (e.g. "api" must not match "api-keys").
function isTabActive(pathname: string, tabPath: string): boolean {
  return pathname === tabPath || pathname.startsWith(`${tabPath}/`);
}

function showsSetupInstructions(user: UseAuthUserResult): boolean {
  return user.is.USER || user.is.CUSTOMER_ADMIN;
}

export const DeveloperToolsView: React.FC = () => {
  const { classes: shell } = usePageShellStyles();
  const { classes } = useStyles();
  const user = useAuthUser();
  const location = useLocation();
  const navigate = useNavigate();

  const basePath = getRoutePath("developer-tools");
  const activeTab = findActiveTab(location.pathname, basePath);

  return (
    <MantineProvider theme={rpaTheme}>
      <div className={shell.container} data-testid="developer-tools-view" aria-label="Developer Tools view">
        <RpiCard className={classes.card} data-testid="developer-tools-card">
          <div className={classes.header}>
            <Heading as="h1" size="6xl">
              Developer Tools
            </Heading>
            <Button
              as="a"
              href={PublicUrl.DOCS_HOME}
              target="_blank"
              rel="noreferrer"
              variant="filled"
              intent="primary"
              iconLeft={<OpenInNewIcon />}
              data-testid="api-documentation-link"
            >
              API Documentation
            </Button>
          </div>

          {showsSetupInstructions(user) && <ApiSetupInstructionsBanner />}

          <Tabs
            value={activeTab}
            classNames={{ tabsList: classes.tabsList, tab: classes.tab }}
            onTabChange={(value) => {
              if (value) navigate(`${basePath}/${value}`);
            }}
          >
            <Tabs.List>
              {DEVELOPER_TOOLS_TABS.map((tab) => (
                <Tabs.Tab key={tab.path} value={tab.path}>
                  {tab.label}
                </Tabs.Tab>
              ))}
            </Tabs.List>

            <Outlet />
          </Tabs>
        </RpiCard>
      </div>
    </MantineProvider>
  );
};
